import {
  Organization,
  RbacGroupRef,
  RbacResourceType,
  RbacRole,
  RoleAccess,
} from '@cast/types';
import {
  RoleBinding,
  RoleBindingStateType,
} from '@cast/types-organization-management';

import { ControlDataBySubjectId } from './types';
import {
  applyOrgToClusterInheritance,
  applyParentOrgToChildOrgInheritance,
  convertAccessMapToControlData,
  getAccessVia,
  getPermissionGroups,
  ResourceAccessEntry,
  ResourceKey,
} from './utils';

export const processRbacData = ({
  roleBindings,
  rolesById,
  organizations,
  currentUserId,
}: {
  roleBindings: RoleBinding[];
  rolesById: Record<string, RbacRole>;
  organizations: Organization[];
  currentUserId?: string;
}): ControlDataBySubjectId => {
  // Maps to track highest role level access per subject per resource
  const subjectAccessMap = new Map<
    string,
    Map<ResourceKey, ResourceAccessEntry>
  >();

  // Map to track user access (separate from group/subject access)
  const userAccessMap = new Map<string, Map<string, RoleAccess>>();

  roleBindings?.forEach((binding) => {
    const roleId = binding.definition?.roleId ?? '';
    const role = rolesById[roleId];
    const roleLevel = role?.level || 0;

    binding.definition?.subjects?.forEach((subject) => {
      const groupRef =
        'group' in subject ? (subject.group as RbacGroupRef) : undefined;
      const groupsRef = groupRef ? [groupRef] : [];

      binding.definition?.scopes?.forEach((scope) => {
        const resourceId = scope.organization?.id || scope.cluster?.id;
        const resourceType: RbacResourceType = scope.organization
          ? 'ORGANIZATION'
          : 'CLUSTER';

        if (!resourceId) return;

        // Add direct access for the subject
        let subjectId: string | undefined;
        if (subject.user) {
          subjectId = subject.user.id;
        } else if (subject.group) {
          subjectId = subject.group.id;
        } else if (subject.serviceAccount) {
          subjectId = subject.serviceAccount.id;
        }

        if (subjectId) {
          if (!subjectAccessMap.has(subjectId)) {
            subjectAccessMap.set(subjectId, new Map());
          }
          const subjectResources = subjectAccessMap.get(subjectId)!;
          const resourceKey: ResourceKey = `${resourceType}:${resourceId}`;
          const existingAccess = subjectResources.get(resourceKey);

          // Only update if this role has a higher level or if there's no existing access
          if (
            !existingAccess ||
            roleLevel > (existingAccess.access.roleLevel || 0)
          ) {
            const access: RoleAccess & { isProcessing?: boolean } = {
              roleId,
              roleLevel,
              roleName: role?.name,
              roleBindingId: binding.id,
              roleBindingName: binding.name,
              inherited: false,
              groupsRef,
              isProcessing: !!binding.status?.some(
                (status) => status.state === RoleBindingStateType.ACCEPTED
              ),
            };

            const accessEntry: ResourceAccessEntry = {
              id: resourceId,
              type: resourceType,
              access,
              accessVia: getAccessVia(access),
              hasAccessTo: getPermissionGroups(rolesById, roleId),
            };

            subjectResources.set(resourceKey, accessEntry);
          }
        }

        // When subject is a group, create user entry for current user with groupsRef
        // (API returns group role bindings when user is a member of that group)
        if (groupRef?.id && currentUserId) {
          if (!userAccessMap.has(currentUserId)) {
            userAccessMap.set(currentUserId, new Map());
          }
          const userResources = userAccessMap.get(currentUserId)!;
          const userResourceKey = `${resourceType}:${resourceId}`;
          const existingUserAccess = userResources.get(userResourceKey);

          if (
            !existingUserAccess ||
            roleLevel > (existingUserAccess.roleLevel || 0)
          ) {
            userResources.set(userResourceKey, {
              roleId,
              roleLevel,
              roleName: role?.name,
              roleBindingId: binding.id,
              roleBindingName: binding.name,
              inherited: false,
              groupsRef: [groupRef],
            });
          }
        }
      });
    });
  });

  // Merge userAccessMap into subjectAccessMap for current user
  if (currentUserId && userAccessMap.has(currentUserId)) {
    if (!subjectAccessMap.has(currentUserId)) {
      subjectAccessMap.set(currentUserId, new Map());
    }
    const subjectResources = subjectAccessMap.get(currentUserId)!;
    const userResources = userAccessMap.get(currentUserId)!;

    userResources.forEach((access, resourceKey) => {
      const existingAccess = subjectResources.get(resourceKey as ResourceKey);
      if (
        !existingAccess ||
        (access.roleLevel || 0) > (existingAccess.access.roleLevel || 0)
      ) {
        const [type, id] = resourceKey.split(':') as [RbacResourceType, string];
        subjectResources.set(resourceKey as ResourceKey, {
          id,
          type,
          access,
          accessVia: getAccessVia(access),
          hasAccessTo: getPermissionGroups(rolesById, access.roleId),
        });
      }
    });
  }

  // Apply org-to-cluster inheritance for each subject
  applyOrgToClusterInheritance(subjectAccessMap);

  // Apply parent organization roles to child organizations
  applyParentOrgToChildOrgInheritance(subjectAccessMap, organizations);

  // Convert to final ControlDataBySubjectId format
  return convertAccessMapToControlData(subjectAccessMap);
};
